/**
 * Route guard.
 *
 * src/App.tsx registered all ~45 routes as bare <Route element={<Page/>}/>.
 * DashboardLayout is NOT a guard: it reads { user, signOut } from useAuth(),
 * never reads `loading`, never checks !user, and uses isAdmin/isInstructor only
 * to decide which sidebar links to render. Protection was per-page and
 * inconsistent, and RLS was the only real backstop.
 *
 * This follows the src/pages/Admin.tsx pattern exactly:
 *   - wait for BOTH auth loading and the role check
 *   - redirect when there is no user
 *   - redirect when the role check RESOLVES false (never on `undefined`)
 *   - render nothing until resolved
 *
 * Roles are resolved server-side through the has_role() RPC rather than from a
 * client-held list, so a tampered client cache cannot grant access. It fails
 * closed: any error resolves to false.
 */

import { ReactNode, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";

export type GuardRole = "admin" | "instructor" | "sponsor";

interface RequireRoleProps {
  children: ReactNode;
  /**
   * Roles that may view this route. Omit to require only that the visitor is
   * signed in. Any ONE of the listed roles is sufficient.
   */
  anyOf?: GuardRole[];
  /** Where to send an authenticated user who lacks the role. */
  fallback?: string;
}

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" aria-label="Loading" />
    </div>
  );
}

export function RequireRole({ children, anyOf, fallback = "/dashboard" }: RequireRoleProps) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const needsRole = !!anyOf && anyOf.length > 0;

  const { data: hasRole, isLoading: roleLoading } = useQuery({
    queryKey: ["require-role", user?.id, anyOf?.join(",") ?? "any"],
    enabled: !!user?.id && needsRole,
    queryFn: async () => {
      if (!user?.id || !anyOf) return false;
      for (const role of anyOf) {
        const { data, error } = await supabase.rpc("has_role", {
          _user_id: user.id,
          _role: role,
        });
        // Fail closed on error rather than treating it as "no role but keep going".
        if (error) {
          console.error("Role check failed:", error);
          return false;
        }
        if (data === true) return true;
      }
      return false;
    },
  });

  // Sponsor-only accounts (sponsor and NOT admin) are confined to /sponsor on
  // sign-in-only routes. On any error in either role check, treat the user as
  // NOT sponsor-only and render the page: a transient network failure must not
  // bounce a regular member into /sponsor (which then fails its own anyOf
  // check and redirects to /dashboard, creating a redirect loop). This is safe
  // because sponsors have no profiles row and RLS protects member data.
  const { data: isSponsorOnly, isLoading: sponsorLoading } = useQuery({
    queryKey: ["require-role", "sponsor-only", user?.id],
    enabled: !!user?.id && !needsRole,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!user?.id) return false;
      const [s, a] = await Promise.all([
        supabase.rpc("has_role", { _user_id: user.id, _role: "sponsor" }),
        supabase.rpc("has_role", { _user_id: user.id, _role: "admin" }),
      ]);
      if (s.error || a.error) {
        console.error("Sponsor role check failed:", s.error ?? a.error);
        return false;
      }
      return s.data === true && a.data !== true;
    },
  });

  useEffect(() => {
    if (!authLoading && !user) return; // handled by the <Navigate> below
    if (!needsRole) return;
    if (roleLoading) return;
    if (hasRole === false) {
      toast.error("You don't have permission to access this page");
      navigate(fallback, { replace: true });
    }
  }, [authLoading, user, needsRole, roleLoading, hasRole, navigate, fallback]);

  // 1. Never decide anything while auth is still restoring.
  if (authLoading) return <FullPageSpinner />;

  // 2. Not signed in: send to /auth, preserving where they were headed so
  //    emailed deep links still work after sign-in.
  if (!user) {
    const redirect = `${location.pathname}${location.search}`;
    return <Navigate to={`/auth?redirect=${encodeURIComponent(redirect)}`} replace />;
  }

  // 3. Signed in, no role requirement: allow.
  if (!needsRole) {
    if (sponsorLoading || isSponsorOnly === undefined) return <FullPageSpinner />;
    if (isSponsorOnly) return <Navigate to="/sponsor" replace />;
    return <>{children}</>;
  }

  // 4. Role check unresolved: render nothing (not the page).
  if (roleLoading || hasRole === undefined) return <FullPageSpinner />;

  // 5. Resolved false: the effect above is redirecting; render nothing meanwhile.
  if (hasRole !== true) return null;

  return <>{children}</>;
}
